/**
 * Home Section Manager
 * Handles switching between about and projects sections on the homepage
 */

class HomeSections {
  constructor() {
    // Section references
    this.aboutSection = document.getElementById('about');
    this.projectsSection = document.getElementById('projects');
    
    // Button references
    this.aboutButton = document.querySelector('[data-nav="about"]');
    this.projectsButton = document.querySelector('[data-nav="projects"]');
    
    // State
    this.currentSection = 'about'; // Default section
    this.projectRowsAnimationTimer = null;
    this.projectRowAnimations = [];
    this.hasInitialized = false;
    
    // Check if there's a hash in the URL immediately
    if (window.location.hash && window.location.hash.length > 1) {
      this.forceProjectsSection();
    } else if (
      new URLSearchParams(window.location.search).has('projects') ||
      new URLSearchParams(window.location.search).has('projets') ||
      new URLSearchParams(window.location.search).has('project')
    ) {
      // Show projects for canonical URLs and legacy French URLs.
      this.currentSection = 'projects';
      
      // Update buttons
      this.updateNavigationState('projects');
      
      // Set sections visibility
      if (this.aboutSection) this.aboutSection.style.display = 'none';
      if (this.projectsSection) this.projectsSection.style.display = 'block';
    } else {
      // Set initial sections visibility
      if (this.aboutSection && this.projectsSection) {
        this.aboutSection.style.display = 'block';
        this.projectsSection.style.display = 'none';
      }
    }
    
    // Initialize
    this.init();
    this.hasInitialized = true;
  }
  
  /**
   * Force the projects section to be visible
   * Used when we have a hash fragment in the URL
   */
  forceProjectsSection() {
    if (this.aboutSection) this.aboutSection.style.display = 'none';
    if (this.projectsSection) this.projectsSection.style.display = 'block';
    this.currentSection = 'projects';
    
    // Update buttons
    this.updateNavigationState('projects');
  }

  updateNavigationState(section) {
    const isAbout = section === 'about';

    if (this.aboutButton) {
      this.aboutButton.classList.toggle('header__menu__button--selected', isAbout);
      if (isAbout) this.aboutButton.setAttribute('aria-current', 'page');
      else this.aboutButton.removeAttribute('aria-current');
    }

    if (this.projectsButton) {
      this.projectsButton.classList.toggle('header__menu__button--selected', !isAbout);
      if (!isAbout) this.projectsButton.setAttribute('aria-current', 'page');
      else this.projectsButton.removeAttribute('aria-current');
    }
  }

  updateSectionMetadata() {
    const siteTitle = document.documentElement.dataset.siteTitle || 'Numeronovantasei';
    const siteDescription = document.documentElement.dataset.siteDescription || '';
    const canonicalUrl = new URL(window.location.pathname, window.location.origin);
    canonicalUrl.search = this.currentSection === 'projects' ? '?projects' : '?about';
    const pageUrl = canonicalUrl.toString();
    document.title = siteTitle;

    const description = document.querySelector('meta[name="description"]');
    if (description) description.content = siteDescription;

    ['og:title', 'twitter:title'].forEach((property) => {
      const element = document.querySelector(`meta[property="${property}"]`);
      if (element) element.content = siteTitle;
    });

    ['og:description', 'twitter:description'].forEach((property) => {
      const element = document.querySelector(`meta[property="${property}"]`);
      if (element) element.content = siteDescription;
    });

    ['og:url', 'twitter:url'].forEach((property) => {
      const element = document.querySelector(`meta[property="${property}"]`);
      if (element) element.content = pageUrl;
    });

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = pageUrl;
  }
  
  /**
   * Initialize section manager
   */
  init() {
    if (!this.aboutSection || !this.projectsSection) {
      return;
    }
    
    // Check URL parameters on page load to set initial section
    this.checkUrlParameters();
    
    // Set up event listeners for buttons
    this.setupEventListeners();

    window.addEventListener('popstate', () => {
      const params = new URLSearchParams(window.location.search);
      const section = window.location.hash || params.has('project') || params.has('projects') || params.has('projets')
        ? 'projects'
        : 'about';
      this.showSection(section, false);
    });
    
    // Make sure correct section is visible
    this.ensureCorrectSectionVisible();
  }
  
  /**
   * Ensure correct section is visible regardless of URL state
   */
  ensureCorrectSectionVisible() {
    this.showSection(this.currentSection, false);
  }
  
  /**
   * Check URL parameters to determine which section to show
   */
  checkUrlParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    const hasHash = window.location.hash && window.location.hash.length > 1;
    const projectSlug = urlParams.get('project') || (hasHash ? window.location.hash.substring(1) : null);
    
    // Project URLs always show the projects section.
    if (projectSlug) {
      this.showSection('projects', false);

      if (hasHash || !urlParams.has('project')) {
        const newUrl = new URL(window.location.href);
        newUrl.search = '';
        newUrl.searchParams.set('project', projectSlug);
        newUrl.hash = '';
        window.history.replaceState(
          { section: 'projects', projectSlug },
          '',
          newUrl.toString()
        );
      }
      
      // Dispatch custom event to trigger project opening
      document.dispatchEvent(new CustomEvent('hashDetected', { 
        detail: { hash: projectSlug } 
      }));
      
      return;
    }
    
    // Check for section parameter
    if (urlParams.has('projects') || urlParams.has('projets')) {
      this.showSection('projects', false);

      if (urlParams.has('projets')) {
        const canonicalUrl = new URL(window.location.href);
        canonicalUrl.search = '?projects';
        window.history.replaceState({ section: 'projects', projectSlug: null }, '', canonicalUrl.toString());
      }
    } else {
      // Default to about section if no parameter or has about
      this.showSection('about', false);
      
      // If we're at the root URL, update to include the about parameter
      if (window.location.pathname === '/' && !urlParams.has('about')) {
        this.updateUrl('about');
      }
    }
  }
  
  /**
   * Set up event listeners for section switching
   */
  setupEventListeners() {
    // Projects button click
    if (this.projectsButton) {
      this.projectsButton.addEventListener('click', () => {
        this.showSection('projects');
      });
    }
    
    // About button click
    if (this.aboutButton) {
      this.aboutButton.addEventListener('click', () => {
        this.showSection('about');
      });
    }
  }

  cancelProjectRowsAnimation() {
    clearTimeout(this.projectRowsAnimationTimer);
    this.projectRowsAnimationTimer = null;
    this.projectRowAnimations.forEach((animation) => animation.cancel());
    this.projectRowAnimations = [];
  }

  animateProjectRows(delay = 0) {
    this.cancelProjectRowsAnimation();

    const rows = [...this.projectsSection.querySelectorAll('.home__section__table-row[role="button"]')];
    if (!rows.length) return;

    const startAnimation = () => {
      this.projectRowAnimations = rows.map((row, index) => row.animate(
        [{ opacity: 0 }, { opacity: 1 }],
        {
          duration: 100,
          delay: index * 45,
          easing: 'ease',
          fill: 'backwards'
        }
      ));
    };

    if (delay > 0) {
      this.projectRowsAnimationTimer = setTimeout(startAnimation, delay);
    } else {
      startAnimation();
    }
  }

  /**
   * Show the specified section and update URL
   */
  showSection(section, updateURL = true) {
    // Update state
    this.currentSection = section;
    
    // Show/hide sections
    if (section === 'about') {
      this.cancelProjectRowsAnimation();
      if (this.aboutSection) this.aboutSection.style.display = 'block';
      if (this.projectsSection) this.projectsSection.style.display = 'none';
      if (this.aboutSection) this.aboutSection.scrollTop = 0;
      
      // Dispatch event to close any open project
      document.dispatchEvent(new CustomEvent('closeProject'));
      
      // Update URL without reloading page
      if (updateURL) {
        this.updateUrl('about');
      }
      
      this.updateNavigationState('about');
      this.updateSectionMetadata();
      
      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('sectionChanged', { 
        detail: { section: 'about' } 
      }));
    } else {
      if (this.aboutSection) this.aboutSection.style.display = 'none';
      if (this.projectsSection) this.projectsSection.style.display = 'block';
      const projectInUrl = new URLSearchParams(window.location.search).has('project') || Boolean(window.location.hash);
      
      // Close only when navigating to the list, not when restoring a project URL.
      if (updateURL || !projectInUrl) {
        document.dispatchEvent(new CustomEvent('closeProject'));
      }
      
      // Update URL without reloading page
      if (updateURL) {
        this.updateUrl('projects');
      }
      
      this.updateNavigationState('projects');
      if (!projectInUrl || updateURL) {
        this.updateSectionMetadata();
        const animationDelay = projectInUrl
          ? 280
          : (this.hasInitialized ? 0 : 120);
        this.animateProjectRows(animationDelay);
      }

      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('sectionChanged', { 
        detail: { section: 'projects' } 
      }));
    }
  }
  
  /**
   * Update URL with the current section parameter
   */
  updateUrl(section) {
    // Create a new URL object to properly handle all URL parts
    const currentUrl = new URL(window.location.href);

    currentUrl.search = `?${section}`;
    currentUrl.hash = '';
    
    const newUrl = currentUrl.toString();
    
    // Update browser history without reloading
    window.history.pushState({
      section: section === 'projects' ? 'projects' : 'about',
      projectSlug: null
    }, '', newUrl);
  }
}

// Create instance only if on homepage
if (document.querySelector('.home')) {
  const homeSections = new HomeSections();
} else {
  // Not on homepage, skipping section initialization
}

export default HomeSections; 